import { SubscriptionStatus } from "@brookbush/database";

import { useUser } from "@clerk/nextjs";
import { useMemo } from "react";

import useSubscriptionDB from "./useSubscriptionDB";
import { isEmployeeUser } from "~/lib/membership.lib";

const useAccess = () => {
	const { subscriptionDB, isLoading, error } = useSubscriptionDB();
	const { user, isLoaded } = useUser();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const memberStates: SubscriptionStatus[] = [
		SubscriptionStatus.ACTIVE,
		SubscriptionStatus.TRIALING,
		SubscriptionStatus.PAST_DUE,
	];

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const formerMemberStates: SubscriptionStatus[] = [
		SubscriptionStatus.CANCELED,
	];

	const isAdmin = useMemo(
		() => subscriptionDB?.organization?.adminId === user?.id,
		[subscriptionDB?.organization?.adminId, user?.id],
	);

	const isEmployee = useMemo(() => isEmployeeUser(user!), [user]);

	const isFacility = user?.publicMetadata?.facility;
	// This is a temporary solution to allow people with gift memberships, grants, and other weird
	// ways of memberships to have access to the site. We don't want to lose these people, so we must
	// do what we can to keep them.

	const isDeveloper = user?.publicMetadata?.isDeveloper === true;

	const hasMemberAccess = useMemo(
		() =>
			user?.publicMetadata?.expiresAt &&
			new Date(user?.publicMetadata?.expiresAt as string) > new Date(),
		[user?.publicMetadata?.expiresAt],
	);

	const isBlockedAccess = Boolean(
		isAdmin && user?.publicMetadata?.blockTeamAdminAccess,
	);

	const isMember = useMemo(
		() =>
			!!(
				isEmployee ||
				(subscriptionDB
					? memberStates.includes(subscriptionDB.status) &&
						!subscriptionDB.isPaused &&
						!isBlockedAccess
					: false) ||
				isFacility ||
				hasMemberAccess
			),
		[
			hasMemberAccess,
			isBlockedAccess,
			isEmployee,
			isFacility,
			memberStates,
			subscriptionDB,
		],
	);

	const isTrialing = useMemo(
		() =>
			subscriptionDB
				? subscriptionDB.status === SubscriptionStatus.TRIALING
				: false,
		[subscriptionDB],
	);

	const isFormerMember = useMemo(
		() =>
			subscriptionDB
				? formerMemberStates.includes(subscriptionDB.status)
				: false,
		[formerMemberStates, subscriptionDB],
	);

	const isPaused = subscriptionDB?.isPaused;

	const accessLevel = useMemo(
		() =>
			isEmployee
				? "Employee"
				: isMember
					? "Member"
					: isPaused
						? "Paused Member"
						: isFormerMember
							? "Former Member"
							: "Guest User",
		[isEmployee, isFormerMember, isMember, isPaused],
	);

	return {
		isAdmin,
		isMember,
		isTrialing,
		isFormerMember,
		// A /subscription error must not be treated as "confirmed non-member"
		// (auto BAM / course gates). Keep access in a loading state until the
		// fetch succeeds or the user signs out.
		isLoadingAccess: isLoading || !!error || !isLoaded,
		isEmployee,
		isDeveloper,
		isPaused,
		subscription: subscriptionDB,
		status: subscriptionDB?.status,
		accessLevel,
		organizationId: subscriptionDB?.organization?.id,
	};
};

export default useAccess;
